import React, { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClipboardCheck,
  FaFileAlt,
  FaGraduationCap,
  FaIdCard,
  FaLaptopHouse,
  FaRoute,
  FaSearch,
  FaUserCheck,
  FaUserGraduate,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import "./Workflow.css";
import CargandoSpinner from "../../0. Utilidades/loadingSpinner";
import { getApiUrl } from "../../../utils/apiUrl";

const CountBadge = ({ value, label }) => (
  <span className="workflow-count" title={`${value} ${label}`}>
    <strong>{value ?? 0}</strong>
    <small>{label}</small>
  </span>
);

const FlowNode = ({ icon, eyebrow, title, text, count, tone = "neutral" }) => (
  <div className={`workflow-node workflow-node-${tone}`}>
    <span className="workflow-node-icon" aria-hidden="true">{icon}</span>
    <div className="workflow-node-copy">
      {eyebrow && <span>{eyebrow}</span>}
      <strong>{title}</strong>
      {text && <p>{text}</p>}
    </div>
    {count}
  </div>
);

const Connector = ({ label }) => (
  <div className="workflow-connector" aria-hidden="true">
    {label && <span>{label}</span>}
    <i />
  </div>
);

const Workflow = () => {
  const expandedByDefault = () => (
    typeof window === "undefined"
    || !window.matchMedia("(max-width: 720px)").matches
  );
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedPaths, setExpandedPaths] = useState(() => ({
    request: expandedByDefault(),
    program: expandedByDefault(),
  }));

  const togglePath = (path) => {
    setExpandedPaths((currentPaths) => ({
      ...currentPaths,
      [path]: !currentPaths[path],
    }));
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadWorkflow = async () => {
      try {
        const response = await fetch(getApiUrl("api/workflow/"), {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        setCounts(await response.json());
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        console.error("No se pudo cargar el flujo de trabajo:", requestError);
        setError("No se pudo cargar el flujo de trabajo.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadWorkflow();
    return () => controller.abort();
  }, []);

  if (loading) {
    return <div className="workflow-loading"><CargandoSpinner /></div>;
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div id="workflow">
      <div className="workflow-intro">
        <span className="workflow-intro-icon" aria-hidden="true"><FaRoute /></span>
        <div>
          <strong>Una Persona, dos caminos para convertirse en Maestra</strong>
          <p>Los formularios y las postulaciones enriquecen una sola ficha; nunca crean perfiles paralelos.</p>
        </div>
      </div>

      <section className="workflow-entry" aria-label="Ingreso a RedMaestra">
        <FlowNode
          icon={<FaLaptopHouse />}
          eyebrow="Canales públicos"
          title="Formularios del Sitio Web"
          text="El RUT y el celular identifican a la persona antes de solicitar nuevos datos."
          tone="entry"
        />
        <Connector />
        <FlowNode
          icon={<FaIdCard />}
          eyebrow="Registro único"
          title="Potenciales Futuras Maestras"
          text="La información queda centralizada y puede crecer mediante formularios, comentarios y documentos."
          tone="interested"
          count={<CountBadge value={counts?.potenciales_maestras} label="personas" />}
        />
      </section>

      <div className="workflow-split-label">
        <span>Dos vías válidas de avance</span>
      </div>

      <div className="workflow-paths">
        <section className="workflow-path workflow-path-request">
          <button
            type="button"
            className="workflow-path-toggle"
            onClick={() => togglePath("request")}
            aria-expanded={expandedPaths.request}
            aria-controls="workflow-request-content"
          >
            <span className="workflow-path-icon"><FaClipboardCheck /></span>
            <div className="workflow-path-heading">
              <small>Vía 1</small>
              <h4>Acreditar experiencia en oficios</h4>
            </div>
            <span className="workflow-path-chevron" aria-hidden="true">
              {expandedPaths.request ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </button>
          {expandedPaths.request && (
            <div className="workflow-path-content" id="workflow-request-content">
              <FlowNode
                icon={<FaUserPlus />}
                title="Solicitud de incorporación"
                text="La persona declara experiencia o el equipo inicia la solicitud."
              />
              <Connector label="Completa antecedentes" />
              <FlowNode
                icon={<FaFileAlt />}
                title="Formulario avanzado"
                text="Agrega trayectoria, antecedentes y documentos a la ficha existente."
                count={<CountBadge value={counts?.solicitudes_esperando_formulario} label="por responder" />}
              />
              <Connector label="Revisión humana" />
              <FlowNode
                icon={<FaSearch />}
                title="Entrevista y validación"
                text="El equipo revisa la información y registra sus observaciones como comentarios."
                count={<CountBadge value={counts?.solicitudes_en_revision} label="en revisión" />}
              />
              <Connector label="Solicitud aceptada" />
              <FlowNode
                icon={<FaCheckCircle />}
                title="Reconocida como Maestra"
                tone="success"
              />
            </div>
          )}
        </section>

        <section className="workflow-path workflow-path-program">
          <button
            type="button"
            className="workflow-path-toggle"
            onClick={() => togglePath("program")}
            aria-expanded={expandedPaths.program}
            aria-controls="workflow-program-content"
          >
            <span className="workflow-path-icon"><FaGraduationCap /></span>
            <div className="workflow-path-heading">
              <small>Vía 2</small>
              <h4>Aprobar un programa</h4>
            </div>
            <span className="workflow-path-chevron" aria-hidden="true">
              {expandedPaths.program ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </button>
          {expandedPaths.program && (
            <div className="workflow-path-content" id="workflow-program-content">
              <FlowNode
                icon={<FaUserCheck />}
                title="Inscripción a la capacitación"
                text="Si existe selección, responde las preguntas específicas; si no, ingresa directamente."
              />
              <Connector label="Cuando corresponde" />
              <FlowNode
                icon={<FaUsers />}
                title="Proceso de selección"
                text="Entrevista y evaluación determinan el ingreso, pero todavía no cambian la clasificación."
                count={<CountBadge value={counts?.postulantes_activas} label="postulantes actuales" />}
              />
              <Connector label="Seleccionada o ingreso directo" />
              <FlowNode
                icon={<FaUserGraduate />}
                title="Participa en el programa"
                text="La selección solo registra su participación; debe completar y aprobar el programa."
                count={<CountBadge value={counts?.participantes_programas_activos} label="cursando" />}
              />
              <Connector label="programa aprobado" />
              <FlowNode
                icon={<FaCheckCircle />}
                title="Se convierte en Maestra"
                tone="success"
              />
            </div>
          )}
        </section>
      </div>

      <section className="workflow-outcome">
        <span className="workflow-outcome-icon" aria-hidden="true"><FaUserGraduate /></span>
        <div>
          <small>Resultado común</small>
          <strong>Comunidad de Maestras</strong>
          <p>La clasificación cambia una sola vez y conserva toda la trayectoria anterior.</p>
        </div>
        <CountBadge value={counts?.maestras} label="maestras" />
      </section>

    </div>
  );
};

export default Workflow;
